import {
  EARTH_CIRCUMFERENCE_METERS,
  type GeoBounds,
  type GeoPoint,
  type LocalBounds2,
  type LocalMetricFrame,
  clampMercatorLatitude,
  createLocalMetricFrame,
  projectGeoPointToFrame,
  unprojectMercator,
  wrapLongitude,
} from "./coordinates.ts";

export const MACROTILE_ZOOM = 14 as const;
export const CHUNK_ZOOM = 17 as const;
export const CHUNKS_PER_MACROTILE_AXIS = 1 << (CHUNK_ZOOM - MACROTILE_ZOOM);
export const CHUNKS_PER_MACROTILE =
  CHUNKS_PER_MACROTILE_AXIS * CHUNKS_PER_MACROTILE_AXIS;
export const DEFAULT_SOURCE_HALO_CHUNKS = 1;

export interface SlippyTileId {
  readonly zoom: number;
  readonly x: number;
  readonly y: number;
}

export interface MacrotileId extends SlippyTileId {
  readonly zoom: typeof MACROTILE_ZOOM;
}

export interface ChunkId extends SlippyTileId {
  readonly zoom: typeof CHUNK_ZOOM;
}

export interface TileBounds {
  readonly geo: GeoBounds;
  readonly mercator: LocalBounds2;
  readonly projectedSpanMeters: number;
  readonly groundSpanMetersAtCenter: {
    readonly x: number;
    readonly z: number;
  };
}

export interface ChunkOffset {
  readonly x: number;
  readonly y: number;
}

export function createTileId(zoom: number, x: number, y: number): SlippyTileId {
  assertZoom(zoom);
  const tileCount = 2 ** zoom;
  if (!Number.isInteger(x) || !Number.isInteger(y)) {
    throw new TypeError("Slippy tile X and Y must be integers");
  }
  if (x < 0 || x >= tileCount || y < 0 || y >= tileCount) {
    throw new RangeError(`Tile ${zoom}/${x}/${y} is outside the slippy grid`);
  }
  return { zoom, x, y };
}

export function createMacrotileId(x: number, y: number): MacrotileId {
  return createTileId(MACROTILE_ZOOM, x, y) as MacrotileId;
}

export function createChunkId(x: number, y: number): ChunkId {
  return createTileId(CHUNK_ZOOM, x, y) as ChunkId;
}

export function isMacrotileId(tile: SlippyTileId): tile is MacrotileId {
  return tile.zoom === MACROTILE_ZOOM && isValidTileIndex(tile);
}

export function isChunkId(tile: SlippyTileId): tile is ChunkId {
  return tile.zoom === CHUNK_ZOOM && isValidTileIndex(tile);
}

export function tileKey(tile: SlippyTileId): string {
  createTileId(tile.zoom, tile.x, tile.y);
  return `${tile.zoom}/${tile.x}/${tile.y}`;
}

export function parseTileKey(key: string): SlippyTileId {
  const match = /^(\d{1,2})\/(\d+)\/(\d+)$/.exec(key);
  if (match === null) {
    throw new TypeError(`Invalid slippy tile key: ${key}`);
  }
  return createTileId(Number(match[1]), Number(match[2]), Number(match[3]));
}

export function tileAtGeoPoint(point: GeoPoint, zoom: number): SlippyTileId {
  assertZoom(zoom);
  const tileCount = 2 ** zoom;
  const longitude = wrapLongitude(point.lon);
  const latitude = clampMercatorLatitude(point.lat);
  const latitudeRadians = (latitude * Math.PI) / 180;
  const fractionalX = ((longitude + 180) / 360) * tileCount;
  const fractionalY =
    ((1 - Math.asinh(Math.tan(latitudeRadians)) / Math.PI) / 2) *
    tileCount;

  return createTileId(
    zoom,
    Math.min(tileCount - 1, Math.max(0, Math.floor(fractionalX))),
    Math.min(tileCount - 1, Math.max(0, Math.floor(fractionalY))),
  );
}

export function macrotileAtGeoPoint(point: GeoPoint): MacrotileId {
  return tileAtGeoPoint(point, MACROTILE_ZOOM) as MacrotileId;
}

export function chunkAtGeoPoint(point: GeoPoint): ChunkId {
  return tileAtGeoPoint(point, CHUNK_ZOOM) as ChunkId;
}

export function chunkToMacrotile(chunk: ChunkId): MacrotileId {
  assertChunkId(chunk);
  return createMacrotileId(
    Math.floor(chunk.x / CHUNKS_PER_MACROTILE_AXIS),
    Math.floor(chunk.y / CHUNKS_PER_MACROTILE_AXIS),
  );
}

export function chunkOffsetInMacrotile(chunk: ChunkId): ChunkOffset {
  assertChunkId(chunk);
  return {
    x: chunk.x % CHUNKS_PER_MACROTILE_AXIS,
    y: chunk.y % CHUNKS_PER_MACROTILE_AXIS,
  };
}

export function chunkFromMacrotileOffset(
  macrotile: MacrotileId,
  offset: ChunkOffset,
): ChunkId {
  assertMacrotileId(macrotile);
  if (
    !Number.isInteger(offset.x) ||
    !Number.isInteger(offset.y) ||
    offset.x < 0 ||
    offset.y < 0 ||
    offset.x >= CHUNKS_PER_MACROTILE_AXIS ||
    offset.y >= CHUNKS_PER_MACROTILE_AXIS
  ) {
    throw new RangeError(
      `Chunk offset must be within 0..${CHUNKS_PER_MACROTILE_AXIS - 1}`,
    );
  }
  return createChunkId(
    macrotile.x * CHUNKS_PER_MACROTILE_AXIS + offset.x,
    macrotile.y * CHUNKS_PER_MACROTILE_AXIS + offset.y,
  );
}

export function chunksInMacrotile(macrotile: MacrotileId): readonly ChunkId[] {
  assertMacrotileId(macrotile);
  const chunks: ChunkId[] = [];
  for (let y = 0; y < CHUNKS_PER_MACROTILE_AXIS; y += 1) {
    for (let x = 0; x < CHUNKS_PER_MACROTILE_AXIS; x += 1) {
      chunks.push(chunkFromMacrotileOffset(macrotile, { x, y }));
    }
  }
  return chunks;
}

/**
 * z17 source coverage for a z14 solve, including a configurable chunk halo.
 * X wraps at the antimeridian; Y is clamped at the Mercator limits.
 */
export function sourceChunksForMacrotile(
  macrotile: MacrotileId,
  haloChunks = DEFAULT_SOURCE_HALO_CHUNKS,
): readonly ChunkId[] {
  assertMacrotileId(macrotile);
  if (!Number.isInteger(haloChunks) || haloChunks < 0) {
    throw new RangeError("Source halo must be a non-negative integer");
  }

  const tileCount = 2 ** CHUNK_ZOOM;
  const firstX = macrotile.x * CHUNKS_PER_MACROTILE_AXIS;
  const firstY = macrotile.y * CHUNKS_PER_MACROTILE_AXIS;
  const chunks: ChunkId[] = [];

  for (
    let y = firstY - haloChunks;
    y < firstY + CHUNKS_PER_MACROTILE_AXIS + haloChunks;
    y += 1
  ) {
    if (y < 0 || y >= tileCount) {
      continue;
    }
    for (
      let x = firstX - haloChunks;
      x < firstX + CHUNKS_PER_MACROTILE_AXIS + haloChunks;
      x += 1
    ) {
      const wrappedX = ((x % tileCount) + tileCount) % tileCount;
      chunks.push(createChunkId(wrappedX, y));
    }
  }
  return chunks;
}

export function tileBounds(tile: SlippyTileId): TileBounds {
  createTileId(tile.zoom, tile.x, tile.y);
  const tileCount = 2 ** tile.zoom;
  const west = tileXToLongitude(tile.x, tileCount);
  const east = tileXToLongitude(tile.x + 1, tileCount);
  const north = tileYToLatitude(tile.y, tileCount);
  const south = tileYToLatitude(tile.y + 1, tileCount);
  const northWest = projectTileCorner(tile.x, tile.y, tileCount);
  const southEast = projectTileCorner(tile.x + 1, tile.y + 1, tileCount);
  const projectedSpanMeters = EARTH_CIRCUMFERENCE_METERS / tileCount;
  const centreLatitude = (north + south) / 2;
  const groundScale = Math.cos((centreLatitude * Math.PI) / 180);

  return {
    geo: { west, south, east, north },
    mercator: {
      minX: northWest.x,
      minZ: northWest.z,
      maxX: southEast.x,
      maxZ: southEast.z,
    },
    projectedSpanMeters,
    groundSpanMetersAtCenter: {
      x: projectedSpanMeters * groundScale,
      z: projectedSpanMeters * groundScale,
    },
  };
}

export function tileCenter(tile: SlippyTileId): GeoPoint {
  const bounds = tileBounds(tile).mercator;
  return unprojectMercator({
    x: (bounds.minX + bounds.maxX) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
  });
}

/** One metric frame shared by the solve macrotile and all of its output chunks. */
export function createMacrotileMetricFrame(
  macrotile: MacrotileId,
): LocalMetricFrame {
  assertMacrotileId(macrotile);
  return createLocalMetricFrame(tileCenter(macrotile));
}

export function tileBoundsInFrame(
  tile: SlippyTileId,
  frame: LocalMetricFrame,
): LocalBounds2 {
  const bounds = tileBounds(tile).geo;
  const northWest = projectGeoPointToFrame(
    { lon: bounds.west, lat: bounds.north },
    frame,
  );
  const southEast = projectGeoPointToFrame(
    { lon: bounds.east, lat: bounds.south },
    frame,
  );
  return {
    minX: northWest.x,
    minZ: northWest.z,
    maxX: southEast.x,
    maxZ: southEast.z,
  };
}

export function neighbouringTile(
  tile: SlippyTileId,
  deltaX: number,
  deltaY: number,
): SlippyTileId | null {
  createTileId(tile.zoom, tile.x, tile.y);
  if (!Number.isInteger(deltaX) || !Number.isInteger(deltaY)) {
    throw new TypeError("Tile neighbour deltas must be integers");
  }
  const tileCount = 2 ** tile.zoom;
  const y = tile.y + deltaY;
  if (y < 0 || y >= tileCount) {
    return null;
  }
  const x = ((tile.x + deltaX) % tileCount + tileCount) % tileCount;
  return createTileId(tile.zoom, x, y);
}

export function assertMacrotileId(
  tile: SlippyTileId,
): asserts tile is MacrotileId {
  if (!isMacrotileId(tile)) {
    throw new TypeError(`Expected a z${MACROTILE_ZOOM} macrotile`);
  }
}

export function assertChunkId(tile: SlippyTileId): asserts tile is ChunkId {
  if (!isChunkId(tile)) {
    throw new TypeError(`Expected a z${CHUNK_ZOOM} render chunk`);
  }
}

function assertZoom(zoom: number): void {
  if (!Number.isInteger(zoom) || zoom < 0 || zoom > 30) {
    throw new RangeError("Slippy zoom must be an integer between 0 and 30");
  }
}

function isValidTileIndex(tile: SlippyTileId): boolean {
  if (!Number.isInteger(tile.x) || !Number.isInteger(tile.y)) {
    return false;
  }
  const tileCount = 2 ** tile.zoom;
  return tile.x >= 0 && tile.x < tileCount && tile.y >= 0 && tile.y < tileCount;
}

function tileXToLongitude(x: number, tileCount: number): number {
  return (x / tileCount) * 360 - 180;
}

function tileYToLatitude(y: number, tileCount: number): number {
  return (
    (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / tileCount))) * 180) /
    Math.PI
  );
}

function projectTileCorner(
  x: number,
  y: number,
  tileCount: number,
): { readonly x: number; readonly z: number } {
  return {
    x: (x / tileCount - 0.5) * EARTH_CIRCUMFERENCE_METERS,
    z: (y / tileCount - 0.5) * EARTH_CIRCUMFERENCE_METERS,
  };
}
